/**
 * Plausible Analytics helper — privacy-safe, consent-gated, production-only.
 *
 * Design:
 *  - Plausible is cookieless and aggregates pageviews per pushState by
 *    default; this helper exposes a tiny custom-event API on top.
 *  - The site already runs a GDPR cookie banner (`CookieBanner.jsx`) that
 *    persists `{ analytics: boolean }` under `dg_cookie_consent_v1` and
 *    fires `dg-cookie-consent-changed` when the user accepts/declines.
 *    We load the Plausible script ONLY after analytics consent is granted
 *    AND we are running on the production domain.
 *  - `trackEvent(name, props)` is always a safe no-op when Plausible is
 *    not loaded (declined, preview, or script blocked).
 */
import { devWarn } from "./devLog";

const PROD_DOMAIN = "doing-good.org";
const SCRIPT_SRC = "https://plausible.io/js/script.js";
const SCRIPT_ID = "plausible-analytics";
const CONSENT_STORAGE_KEY = "dg_cookie_consent_v1";

let _bootstrapped = false;

function isProductionHost() {
  if (typeof window === "undefined") return false;
  return window.location.hostname === PROD_DOMAIN;
}

function hasAnalyticsConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const v = JSON.parse(raw);
    return !!(v && v.analytics === true);
  } catch (err) {
    devWarn("analytics: consent read failed", err);
    return false;
  }
}

function injectScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById(SCRIPT_ID)) return; // never duplicate
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.defer = true;
  s.setAttribute("data-domain", PROD_DOMAIN);
  s.src = SCRIPT_SRC;
  document.head.appendChild(s);

  // Plausible's loader auto-exposes window.plausible, but provide a
  // queueing stub so events fired in the same tick are not lost.
  if (typeof window !== "undefined" && !window.plausible) {
    window.plausible = function plausibleStub() {
      (window.plausible.q = window.plausible.q || []).push(arguments);
    };
  }
}

/**
 * Initialise once at app start. Idempotent. Listens for consent changes so
 * accepting analytics from the banner immediately enables tracking without
 * a reload.
 */
export function initAnalytics() {
  if (_bootstrapped) return;
  _bootstrapped = true;
  if (!isProductionHost()) return;

  if (hasAnalyticsConsent()) {
    injectScript();
  }

  if (typeof window !== "undefined") {
    window.addEventListener("dg-cookie-consent-changed", (e) => {
      const detail = e && e.detail;
      if (detail && detail.analytics === true) {
        injectScript();
      }
    });
  }
}

/**
 * Safe no-op event tracker. Never throws.
 *
 * @param {string} name  short, fixed event name (e.g. "Opportunity CTA Click")
 * @param {object} [props] only short, non-personal string/number values
 */
export function trackEvent(name, props) {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.plausible !== "function") return;
    const safeProps = sanitizeProps(props);
    if (safeProps && Object.keys(safeProps).length > 0) {
      window.plausible(name, { props: safeProps });
    } else {
      window.plausible(name);
    }
  } catch (err) {
    // Analytics must never break the UX.
    devWarn("analytics: trackEvent failed", err);
  }
}

// Whitelist of property keys we ever send to Plausible. Anything outside
// the whitelist is dropped — this is the last line of defence against
// accidentally shipping personal data.
const ALLOWED_PROP_KEYS = new Set([
  "opportunity_type",
  "city",
  "cause",
  "domain",
  "from",
  "to",
  "page_type",
  "logged_in",
  "filter_type",
  "surface",
]);

function sanitizeProps(props) {
  if (!props || typeof props !== "object") return null;
  const out = {};
  for (const k of Object.keys(props)) {
    if (!ALLOWED_PROP_KEYS.has(k)) continue;
    const v = props[k];
    if (v === null || v === undefined) continue;
    if (typeof v === "boolean") {
      out[k] = v ? "true" : "false";
      continue;
    }
    if (typeof v === "number" && Number.isFinite(v)) {
      out[k] = String(v);
      continue;
    }
    if (typeof v === "string") {
      // Hard length cap; trim; drop suspicious values that look like emails
      // or URLs with query strings (defence-in-depth against personal data).
      const trimmed = v.trim().slice(0, 64);
      if (!trimmed) continue;
      if (trimmed.includes("@")) continue;
      if (trimmed.includes("?")) continue;
      out[k] = trimmed;
    }
  }
  return out;
}

/**
 * Helper for outbound-CTA tracking: strips a full external URL down to
 * its bare host so we never log query strings, paths or tokens.
 *
 * "https://www.betterplace.org/de/projects/123?ref=abc" -> "betterplace.org"
 */
export function safeDomainFromUrl(url) {
  try {
    if (!url || typeof url !== "string") return null;
    const u = new URL(url);
    const host = (u.hostname || "").replace(/^www\./i, "");
    return host || null;
  } catch (err) {
    devWarn("analytics: safeDomainFromUrl failed", err);
    return null;
  }
}
